// The floor of the map: the ground the towers stand on and the road the enemy walks, drawn as one
// surface by a shader instead of a painted sheet with strips laid over it. Everything is made
// from noise in tile space, so nothing repeats and no seam lines up: meadow grass in swells of
// fresher and drier green with straw and bare earth in ragged patches; and along the road, the
// grass trodden to dust, a gravel shoulder, and worn asphalt with its paint rubbed off, tar
// patches, cracks and pale wheel paths. The road comes to the shader as its legs in tiles, so a
// bend is filleted and the asphalt's edge is crisp at any zoom. The floor runs a few tiles past
// the map and gives out there into the churned earth of the countryside, so the plan has no
// square edge. The countryside's highways take the same asphalt (roadStripMaterial) and meet it
// at the gates.
import * as THREE from 'three';
import { GATE, MAP_SIZE, ROAD_WIDTH, roadsOf, type TdMap } from '@thrash/sim';
import { photo } from './ground3d.ts';

/** How far the floor runs past the map edge, in tiles: the ground out there is flat (see outskirts3d's terrain). */
export const FLOOR_PAD = 6;
/** Tiles of road to one cycle of the centre dash. */
const DASH = 3;
const MAX_SEG = 16;
/**
 * How far along its road a highway is at the floor's edge (FLOOR_PAD tiles out). A map's road
 * starts nine tiles out (td-maps.ts), so it is three tiles along there; the countryside's strip
 * counts from the same number outward, and the two meet in step at the gate.
 */
export const HIGHWAY_ALONG0 = 3;

const LIB = /* glsl */ `
  float fHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float fNoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(fHash(i), fHash(i + vec2(1.0, 0.0)), u.x), mix(fHash(i + vec2(0.0, 1.0)), fHash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float fFbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int k = 0; k < 4; k++) { v += a * fNoise(p); p = p * 2.07 + 13.7; a *= 0.5; }
    return v;
  }
  // a colour given in sRGB bytes, in the shader's linear light
  vec3 col(float r, float g, float b) { return pow(vec3(r, g, b) / 255.0, vec3(2.2)); }
  float smin(float a, float b, float k) { float h = max(k - abs(a - b), 0.0) / k; return min(a, b) - h * h * k * 0.25; }
  float band(float x, float w, float aa) { return 1.0 - smoothstep(w - aa, w + aa, abs(x)); }

  // Worn asphalt. p: tiles, for the noise. d: distance to the road's centre line (smooth round the
  // bends). across: signed distance to the nearest leg. along: tiles along the road. toEnd: tiles
  // to the nearest end of the leg, so the dash stops short of a bend. hw: half the road's width.
  vec3 asphalt(vec2 p, float d, float across, float along, float toEnd, float hw) {
    float aa = fwidth(d) + 0.002;
    vec3 c = col(62.0, 61.0, 58.0);
    // long stretches bleached a little more or less by the sun
    c *= 0.92 + fFbm(p * 0.3 + 3.1) * 0.16;
    // the aggregate
    float grit = fNoise(p * 24.0) * 0.6 + fNoise(p * 47.0) * 0.4;
    c *= 0.9 + grit * 0.2;
    // wheel paths worn pale, and rubbed dark with rubber where the traffic brakes into a bend
    float path = 1.0 - smoothstep(0.08, 0.3, abs(abs(across) - hw * 0.55));
    c *= 1.0 + 0.1 * path;
    c *= 1.0 - 0.14 * path * (1.0 - smoothstep(1.0, 4.5, toEnd));
    // the crown dark with oil
    c *= 1.0 - 0.06 * (1.0 - smoothstep(0.0, 0.2, abs(across)));
    // a seam of fresh tar along one wheel path, here and there
    float seam = band(abs(across) - hw * 0.55, 0.03, aa) * smoothstep(0.55, 0.62, fFbm(p * 0.25 + 23.0));
    c = mix(c, col(44.0, 43.0, 41.0), seam * 0.8);
    // the paint: a line along each edge and a broken dash down the crown, a little worn
    float wear = 0.55 + 0.45 * smoothstep(0.3, 0.6, fFbm(p * 1.3 + 41.0));
    float edge = band(d - (hw - 0.17), 0.035, aa);
    c = mix(c, col(206.0, 200.0, 178.0), edge * wear * 0.85);
    float cyc = fract(along / ${DASH.toFixed(1)}) * ${DASH.toFixed(1)};
    float on = smoothstep(0.0, 0.05, cyc) * (1.0 - smoothstep(${(DASH / 2).toFixed(2)} - 0.05, ${(DASH / 2).toFixed(2)}, cyc));
    float dash = on * band(across, 0.05, aa) * smoothstep(1.0, 1.6, toEnd);
    c = mix(c, col(204.0, 170.0, 64.0), dash * wear * 0.85);
    // the edge crumbling away
    c *= 1.0 - 0.25 * smoothstep(hw - 0.08, hw, d);
    return c;
  }

  // The land as a farmer left it: a patchwork of parcels, each worked its own way. Columns of
  // parcels run north-south at jittered spacings, and each column is cut into parcels at its own
  // jittered heights, so no boundary runs straight through the plan.
  float colEdge(float i) { return i * 15.0 + (fHash(vec2(i, 1.7)) - 0.5) * 8.0; }
  float rowEdge(float i, float j) { return j * 12.0 + (fHash(vec2(i, j) + 0.3) - 0.5) * 7.0; }
  // a thin dark line along a hedge, and two pale ruts along a track
  vec3 pasture(vec2 p, float tone, float fine) {
    return mix(col(70.0, 98.0, 46.0), col(90.0, 118.0, 54.0), tone) * (0.9 + fine * 0.2);
  }
  vec3 fields(vec2 p, float fine, out float bump) {
    float i = floor(p.x / 15.0);
    if (p.x < colEdge(i)) i -= 1.0; else if (p.x >= colEdge(i + 1.0)) i += 1.0;
    float j = floor(p.y / 12.0);
    if (p.y < rowEdge(i, j)) j -= 1.0; else if (p.y >= rowEdge(i, j + 1.0)) j += 1.0;
    float x0 = colEdge(i), x1 = colEdge(i + 1.0), y0 = rowEdge(i, j), y1 = rowEdge(i, j + 1.0);
    vec2 id = vec2(i, j);
    float kind = fHash(id + 0.5), tone = fHash(id + 0.9);
    // the stripes of the work run the long way of the parcel
    bool tall = (y1 - y0) > (x1 - x0);
    float run = tall ? p.x - x0 : p.y - y0;
    float slow = fFbm(p * 0.12 + 5.0);
    vec3 c;
    bump = 1.0;
    if (kind < 0.5) {
      // pasture, grazed uneven
      c = pasture(p, tone * 0.6 + slow * 0.4, fine);
    } else if (kind < 0.72) {
      // a meadow mown in strips
      float s = 0.5 + 0.5 * sin(run * 6.2832 / 1.3);
      c = pasture(p, tone * 0.5 + 0.3, fine) * (0.93 + 0.14 * smoothstep(0.3, 0.7, s));
    } else if (kind < 0.84) {
      // hay, cut and lying in rows
      float s = 0.5 + 0.5 * sin(run * 6.2832 / 1.1);
      vec3 straw = mix(col(138.0, 130.0, 82.0), col(154.0, 146.0, 94.0), tone) * (0.92 + fine * 0.16);
      c = mix(straw, straw * 1.1, smoothstep(0.35, 0.65, s) * 0.7);
      bump = 0.6;
    } else if (kind < 0.93) {
      // ploughed, the furrows in the sun
      float s = 0.5 + 0.5 * sin(run * 6.2832 / 0.28);
      c = mix(col(98.0, 80.0, 58.0), col(114.0, 94.0, 66.0), tone * 0.5 + 0.2) * (0.86 + 0.24 * s) * (0.9 + fine * 0.2);
      bump = 0.4;
    } else {
      // a young crop in rows, soil between
      float s = 0.5 + 0.5 * sin(run * 6.2832 / 0.4);
      c = mix(col(96.0, 76.0, 52.0), col(78.0, 112.0, 46.0), smoothstep(0.3, 0.6, s) * 0.85) * (0.9 + fine * 0.2);
      bump = 0.6;
    }
    // the boundaries: a hedge or a track, or just the change of the ground
    float dl = p.x - x0, dr = x1 - p.x, dt = p.y - y0, db = y1 - p.y;
    float e = min(min(dl, dr), min(dt, db));
    float style = e == dl ? fHash(vec2(i, 99.0)) : e == dr ? fHash(vec2(i + 1.0, 99.0)) : e == dt ? fHash(vec2(i, j) + 7.0) : fHash(vec2(i, j + 1.0) + 7.0);
    float ragged = (fNoise(p * 5.0) - 0.5) * 0.12;
    if (style < 0.55) {
      // a hedge, with the rough grass under it
      float hedge = 1.0 - smoothstep(0.16, 0.3, e + ragged);
      float under = 1.0 - smoothstep(0.3, 0.7, e + ragged);
      c = mix(c, pasture(p, 0.2, fine) * 0.85, under);
      c = mix(c, col(42.0, 66.0, 32.0) * (0.8 + 0.4 * fNoise(p * 9.0)), hedge);
      bump = mix(bump, 1.0, under);
    } else if (style < 0.8) {
      // a farm track: pale dirt with two ruts
      float track = 1.0 - smoothstep(0.34, 0.5, e + ragged);
      float ruts = band(e - 0.16, 0.05, 0.03);
      vec3 dirt = col(132.0, 116.0, 84.0) * (0.9 + fine * 0.2);
      c = mix(c, mix(dirt, dirt * 0.78, ruts), track);
      bump = mix(bump, 0.5, track);
    }
    return c;
  }
`;

/** The road as the shader takes it: up to MAX_SEG legs, each (ax, ay, bx, by) in tiles, with where along the road it starts and how long it is. */
interface RoadUniforms {
  uSeg: { value: Float32Array };
  uSegInfo: { value: Float32Array };
  uSegCount: { value: number };
  uHalf: { value: number };
}

function roadUniforms(): RoadUniforms {
  return {
    uSeg: { value: new Float32Array(MAX_SEG * 4) },
    uSegInfo: { value: new Float32Array(MAX_SEG * 2) },
    uSegCount: { value: 0 },
    uHalf: { value: ROAD_WIDTH / 2 },
  };
}

/** The legs of every road on the map, and a stub of highway in through each gate the map does not use (or every gate, with no map). */
function legs(map: TdMap | null): { a: [number, number]; b: [number, number]; along0: number }[] {
  const out: { a: [number, number]; b: [number, number]; along0: number }[] = [];
  const used = new Set<string>();
  if (map) {
    for (const pts of roadsOf(map)) {
      let along = 0;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!, b = pts[i]!;
        out.push({ a, b, along0: along });
        along += Math.hypot(b[0] - a[0], b[1] - a[1]);
      }
      const [gx, gy] = pts[0]!;
      for (const [name, g] of Object.entries(GATE)) if (Math.abs(gx - g.x) < 1 || Math.abs(gy - g.y) < 1) used.add(name);
    }
  }
  for (const [name, g] of Object.entries(GATE)) {
    if (used.has(name)) continue;
    const ox = g.x === MAP_SIZE ? 1 : 0, oy = g.y === 0 ? -1 : g.y === MAP_SIZE ? 1 : 0;
    const cx = g.x + (ox ? 0 : 0.5), cy = g.y + (oy ? 0 : 0.5);
    // in to a tile short of the edge, so the round end of the stub reaches the edge and no further;
    // its dash is in step with the strip beyond the floor's edge, a tile along from where it starts
    out.push({ a: [cx + ox * (FLOOR_PAD + 1), cy + oy * (FLOOR_PAD + 1)], b: [cx + ox, cy + oy], along0: HIGHWAY_ALONG0 - 1 });
  }
  return out;
}

/** Points the floor's road at a map's roads (or, with none, at the highway stubs alone). */
export function setFloorRoad(mat: THREE.MeshStandardMaterial, map: TdMap | null): void {
  const u = mat.userData.road as RoadUniforms;
  const all = legs(map).slice(0, MAX_SEG);
  all.forEach((l, i) => {
    u.uSeg.value.set([l.a[0], l.a[1], l.b[0], l.b[1]], i * 4);
    u.uSegInfo.value.set([l.along0, Math.hypot(l.b[0] - l.a[0], l.b[1] - l.a[1])], i * 2);
  });
  u.uSegCount.value = all.length;
}

/**
 * The floor's material. The mesh's uv is tile coordinates (x east, y south, unrotated), and
 * the photographed grass's normal map rides on it for the sun to catch; the colour is all shader.
 */
export function floorMaterial(): THREE.MeshStandardMaterial {
  const normal = photo('sparse_grass', 'nor').clone();
  normal.repeat.set(1 / 6, 1 / 6);
  const mat = new THREE.MeshStandardMaterial({ roughness: 1, side: THREE.DoubleSide, normalMap: normal, normalScale: new THREE.Vector2(1.1, 1.1) });
  const road = roadUniforms();
  const uniforms = { ...road, uMap: { value: MAP_SIZE }, uDirt: { value: photo('dirt_aerial_02', 'diff') } };
  mat.userData.road = road;
  mat.onBeforeCompile = s => {
    Object.assign(s.uniforms, uniforms);
    s.vertexShader = s.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vTile;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvTile = uv;');
    s.fragmentShader = s.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec2 vTile;
        uniform vec4 uSeg[${MAX_SEG}];
        uniform vec2 uSegInfo[${MAX_SEG}];
        uniform int uSegCount;
        uniform float uHalf, uMap;
        uniform sampler2D uDirt;
        ${LIB}`)
      .replace('void main() {', 'void main() {\n float gBump = 1.0;')
      .replace('#include <map_fragment>', `{
        vec2 p = vTile;
        // the road: the smooth distance to it for its edges, and the nearest leg for the paint
        float d = 1e5, dn = 1e5, along = 0.0, toEnd = 1e5, across = 1e5;
        for (int i = 0; i < ${MAX_SEG}; i++) {
          if (i >= uSegCount) break;
          vec2 a = uSeg[i].xy, ab = uSeg[i].zw - a;
          float len = uSegInfo[i].y;
          float t = clamp(dot(p - a, ab) / (len * len), 0.0, 1.0);
          float di = length(p - (a + ab * t));
          d = smin(d, di, 1.1);
          if (di < dn) {
            dn = di;
            along = uSegInfo[i].x + t * len;
            toEnd = min(t, 1.0 - t) * len;
            vec2 n = ab / len;
            across = (p.x - a.x) * n.y - (p.y - a.y) * n.x;
          }
        }
        float aa = fwidth(d) + 0.002;
        float lump = fFbm(p * 0.17 + 1.0);
        float grit = fNoise(p * 19.0) * 0.6 + fNoise(p * 37.0) * 0.4;
        float fine = fNoise(p * 7.0) * 0.55 + fNoise(p * 15.0) * 0.45;
        float fieldBump;
        vec3 c = fields(p, fine, fieldBump);
        // the odd patch worn bare, where something stood or was dragged
        float bare = smoothstep(0.76, 0.84, fFbm(p * 0.35 + 31.0));
        vec3 earth = col(82.0, 68.0, 48.0) * (0.78 + grit * 0.44) * (0.85 + 0.3 * lump);
        c = mix(c, earth, bare);
        // sand along the shore, where the land meets the water on the west
        float sand = 1.0 - smoothstep(0.9, 2.4, p.x + (fFbm(p * 0.6 + 4.0) - 0.5) * 1.4);
        c = mix(c, col(186.0, 172.0, 138.0) * (0.84 + grit * 0.3), sand);
        // the verge: grass trodden to dust beside the road, in a ragged band
        float dz = d + (fFbm(p * 1.2 + 5.0) - 0.5) * 1.1;
        float tramp = 1.0 - smoothstep(uHalf + 0.1, uHalf + 0.8, dz);
        vec3 dust = col(112.0, 96.0, 66.0) * (0.8 + grit * 0.4);
        c = mix(c, dust, tramp * 0.8);
        // the gravel shoulder
        float shoulder = 1.0 - smoothstep(uHalf + 0.1, uHalf + 0.32, d + (fNoise(p * 5.0) - 0.5) * 0.14);
        vec3 gravel = col(148.0, 140.0, 124.0) * (0.72 + grit * 0.56);
        c = mix(c, gravel, shoulder);
        // the map's edge: the meadow gives out into the churned earth of the countryside
        float outside = max(max(-p.y, p.y - uMap), p.x - uMap);
        float fade = smoothstep(1.0, 4.5, outside + (fFbm(p * 0.4 + 2.0) - 0.5) * 3.0);
        vec3 churned = texture2D(uDirt, p / 14.0).rgb * col(104.0, 88.0, 72.0) * 1.1 * (0.9 + 0.2 * lump);
        c = mix(c, churned, fade);
        // the ground falls away a little to the road: a soft shadow line along the shoulder
        c *= 1.0 - 0.16 * (1.0 - smoothstep(uHalf + 0.1, uHalf + 0.7, d));
        // and the asphalt over all of it
        float tarmac = 1.0 - smoothstep(uHalf - aa, uHalf + aa, d);
        if (tarmac > 0.0) c = mix(c, asphalt(p, d, across, along, toEnd, uHalf), tarmac);
        gBump = mix(mix(fieldBump, 0.45, max(max(bare, sand), max(tramp, fade))), 0.0, tarmac);
        diffuseColor.rgb *= c;
      }`)
      .replace('#include <normal_fragment_maps>', THREE.ShaderChunk.normal_fragment_maps.replace(/normalScale/g, '(normalScale * gBump)'));
  };
  mat.customProgramCacheKey = () => 'floor';
  return mat;
}

/**
 * The asphalt of the countryside's roads, for a strip mesh: its `uv` is tile coordinates for the
 * noise, and `aRoad` is (tiles along, tiles across from the centre line, half the width).
 */
export function roadStripMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({ roughness: 0.95, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
  mat.onBeforeCompile = s => {
    s.vertexShader = s.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 aRoad;\nvarying vec2 vTile;\nvarying vec3 vRoad;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvTile = uv;\nvRoad = aRoad;');
    s.fragmentShader = s.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec2 vTile;\nvarying vec3 vRoad;\n${LIB}`)
      .replace('#include <map_fragment>', 'diffuseColor.rgb *= asphalt(vTile, abs(vRoad.y), vRoad.y, vRoad.x, 1e3, vRoad.z);');
  };
  mat.customProgramCacheKey = () => 'road-strip';
  return mat;
}
